import { useAppDispatch } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { setContactFormModalOpen } from '@/app/redux/slices/modalsSlice';
import { ActivationChecklistContent } from '@/common/ui/ActivationChecklist';
import {
  BookIcon,
  ListChecksIcon,
  PhoneIcon,
  PlayCircleIcon,
  QuestionIcon,
  UsersIcon,
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem } from '@shadcn/components/sidebar';
import { useState } from 'react';
import trackMixpanelEvent from '../utils/mixpanel';

export default function HelpMenu() {
  const dispatch = useAppDispatch();
  const { organisation } = useOrganisation();
  const [open, setOpen] = useState(false);
  const [gettingStartedOpen, setGettingStartedOpen] = useState(false);
  const [gettingStartedItemIdx, setGettingStartedItemIdx] = useState(0);

  const handleContactClick = () => {
    trackMixpanelEvent('Clicked Contact from Dashboard', {
      source: 'sidebar_help_dropdown',
    });
    setOpen(false);
    dispatch(setContactFormModalOpen(true));
  };

  const handleVideoGuidesClick = () => {
    trackMixpanelEvent('Clicked Video Guides from Dashboard', {
      source: 'sidebar_help_dropdown',
    });
    setOpen(false);
    window.open(
      'https://www.youtube.com/playlist?list=PLx3C9XERVNKv1kWW3xatlUF-94-Ob5n4j',
      '_blank',
    );
  };

  const handleCallWithRupertClick = () => {
    trackMixpanelEvent('Clicked Call with Rupert from Dashboard', {
      source: 'sidebar_help_dropdown',
    });
    setOpen(false);
    window.open(
      'https://calendly.com/rupert-spellcaster/spellcaster-pilot-call',
      '_blank',
    );
  };

  const handleGettingStartedClick = () => {
    trackMixpanelEvent('Clicked Getting Started from Dashboard', {
      source: 'sidebar_help_dropdown',
    });
    setOpen(false);
    setGettingStartedItemIdx(0);
    setGettingStartedOpen(true);
  };

  return (
    <>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              onClick={() =>
                trackMixpanelEvent('Clicked Need Help from Dashboard', {
                  source: 'sidebar',
                })
              }
              tooltip="Need Help?"
            >
              <QuestionIcon weight="light" />
              <span>Need Help?</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-72 mb-1">
            {organisation?.activationChecklist.isDismissed && (
              <DropdownMenuItem
                onClick={handleGettingStartedClick}
                className="flex flex-col items-start p-3"
              >
                <div className="flex items-center">
                  <ListChecksIcon size={18} className="mr-2" />
                  <span className="font-medium">Getting started</span>
                </div>
                <span className="text-xs text-gray-600 mt-1 ml-6">
                  Revisit the setup checklist
                </span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => {
                setOpen(false);
                window.open(
                  'https://spellcaster.education/documentation',
                  '_blank',
                );
              }}
              className="flex flex-col items-start p-3"
            >
              <div className="flex items-center">
                <BookIcon size={18} className="mr-2" />
                <span className="font-medium">Spellcaster Docs</span>
              </div>
              <span className="text-xs text-gray-600 mt-1 ml-6">
                Comprehensive guides and documentation
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleVideoGuidesClick}
              className="flex flex-col items-start p-3"
            >
              <div className="flex items-center">
                <PlayCircleIcon size={18} className="mr-2" />
                <span className="font-medium">Video Guides</span>
              </div>
              <span className="text-xs text-gray-600 mt-1 ml-6">
                Step-by-step video tutorials
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleContactClick}
              className="flex flex-col items-start p-3"
            >
              <div className="flex items-center">
                <UsersIcon size={18} className="mr-2" />
                <span className="font-medium">Get in Touch</span>
              </div>
              <span className="text-xs text-gray-600 mt-1 ml-6">
                Have a question? Let us know!
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleCallWithRupertClick}
              className="flex flex-col items-start p-3"
            >
              <div className="flex items-center">
                <PhoneIcon size={18} className="mr-2" />
                <span className="font-medium">Jump on a call with Rupert</span>
              </div>
              <span className="text-xs text-gray-600 mt-1 ml-6">
                Schedule a demo or get direct support
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <Dialog open={gettingStartedOpen} onOpenChange={setGettingStartedOpen}>
        <DialogContent className="max-w-[840px] p-2 bg-neutral-50">
          <DialogHeader className="sr-only">
            <DialogTitle>Getting started</DialogTitle>
          </DialogHeader>
          <ActivationChecklistContent
            currentItemIdx={gettingStartedItemIdx}
            setCurrentItemIdx={setGettingStartedItemIdx}
            showDismiss={false}
            onDismissClick={() => {}}
            onFinish={() => {
              trackMixpanelEvent('Getting Started Finish Clicked');
              setGettingStartedOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
